// ABOUTME: Sticky horizontal navigation bar that displays week pills for scrolling through data
// ABOUTME: Provides click-to-scroll functionality to navigate to specific weeks in the list

import React, { useState, useEffect, useRef } from 'react';
import { StorageManager } from '../services/storage/StorageManager';
import { DataItem } from '../types';

interface WeekData {
    label: string;
    timestamp: number; // Representative timestamp for the week
    startOfWeek: number; // Start of week timestamp for consistent grouping
    itemCount: number;
}

interface WeekNavigationBarProps {
    onWeekClick: (timestamp: number) => void;
    currentWeek?: string; // Currently visible week in the list
}

const WeekNavigationBar: React.FC<WeekNavigationBarProps> = ({ onWeekClick, currentWeek }) => {
    const [weeks, setWeeks] = useState<WeekData[]>([]);
    const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Helper function to get week label (matches List.tsx logic)
    const getWeekLabel = (timestamp: number): string => {
        const date = new Date(timestamp);
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
        
        const now = new Date();
        const diffTime = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 7) {
            return "This Week";
        } else if (diffDays < 14) {
            return "Last Week";
        } else {
            const month = startOfWeek.toLocaleDateString('en-US', { month: 'long' });
            const day = startOfWeek.getDate();
            return `Week of ${month} ${day}`;
        }
    };

    // Helper function to get start of week timestamp
    const getStartOfWeek = (timestamp: number): number => {
        const date = new Date(timestamp);
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
        startOfWeek.setHours(0, 0, 0, 0); // Beginning of the day
        return startOfWeek.getTime();
    };

    // Load and process week data
    useEffect(() => {
        const loadWeekData = async () => {
            setIsLoading(true);
            try {
                const provider = await StorageManager.getProvider();
                
                // Get all workout and meal items
                const queryOptions = {
                    filterBy: 'workout,meal',
                    sortBy: 'timestamp' as keyof DataItem,
                    sortOrder: 'desc' as 'asc' | 'desc'
                };
                
                const totalCount = await provider.getTotalItemCount(queryOptions);
                if (totalCount === 0) {
                    setWeeks([]);
                    setIsLoading(false);
                    return;
                }

                // Load all items to analyze weeks (for better performance, we could paginate this)
                const allItems = await provider.getItems(1, totalCount, queryOptions);
                
                // Group items by week
                const weekMap = new Map<number, { label: string; timestamp: number; itemCount: number }>();
                
                allItems.forEach(item => {
                    const startOfWeek = getStartOfWeek(item.timestamp);
                    const weekLabel = getWeekLabel(item.timestamp);
                    
                    if (weekMap.has(startOfWeek)) {
                        const existing = weekMap.get(startOfWeek)!;
                        weekMap.set(startOfWeek, {
                            ...existing,
                            itemCount: existing.itemCount + 1
                        });
                    } else {
                        weekMap.set(startOfWeek, {
                            label: weekLabel,
                            timestamp: item.timestamp, // Use actual item timestamp
                            itemCount: 1
                        });
                    }
                });

                // Convert to sorted array (newest first)
                const weekData: WeekData[] = Array.from(weekMap.entries())
                    .map(([startOfWeek, data]) => ({
                        ...data,
                        startOfWeek
                    }))
                    .sort((a, b) => b.startOfWeek - a.startOfWeek);

                setWeeks(weekData);
            } catch (error) {
                console.error('Failed to load week data:', error);
                setWeeks([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadWeekData();
    }, []);

    // Update selected week when currentWeek prop changes
    useEffect(() => {
        if (currentWeek) {
            setSelectedWeek(currentWeek);
        }
    }, [currentWeek]);

    const handleWeekClick = (week: WeekData) => {
        setSelectedWeek(week.label);
        onWeekClick(week.timestamp);
    };

    const scrollToSelected = () => {
        if (selectedWeek && scrollContainerRef.current) {
            const selectedButton = scrollContainerRef.current.querySelector(`[data-week="${selectedWeek}"]`) as HTMLElement;
            if (selectedButton) {
                selectedButton.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        }
    };

    // Auto-scroll to selected week when it changes
    useEffect(() => {
        const timer = setTimeout(scrollToSelected, 100);
        return () => clearTimeout(timer);
    }, [selectedWeek]);

    if (isLoading) {
        return (
            <div className="sticky top-0 z-50 bg-slate-900 border-b border-slate-700 px-4 py-3">
                <div className="flex items-center space-x-2">
                    <div className="w-16 h-8 bg-slate-700  animate-pulse"></div>
                    <div className="w-20 h-8 bg-slate-700  animate-pulse"></div>
                    <div className="w-24 h-8 bg-slate-700  animate-pulse"></div>
                </div>
            </div>
        );
    }

    if (weeks.length === 0) {
        return null; // Don't show navigation if no weeks exist
    }

    return (
        <div className="sticky top-0 z-50 bg-slate-900 border-b border-slate-700 px-4 py-3">
            <div 
                ref={scrollContainerRef}
                className="flex space-x-2 overflow-x-auto scroll-smooth"
                style={{ 
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }}
            >
                {weeks.map((week) => (
                    <button
                        key={week.startOfWeek}
                        data-week={week.label}
                        onClick={() => handleWeekClick(week)}
                        className={`
                            flex-shrink-0 px-4 py-2  text-sm font-medium transition-all duration-200
                            ${selectedWeek === week.label
                                ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white'
                            }
                        `}
                    >
                        <span className="block">{week.label}</span>
                        <span className="block text-xs opacity-75">
                            {week.itemCount} item{week.itemCount !== 1 ? 's' : ''}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default WeekNavigationBar;